import { useCallback, useEffect, useRef, useState } from 'react';
import {
  data,
  Form,
  Link,
  type ShouldRevalidateFunction,
  useFetcher,
  useLoaderData,
  useSearchParams,
} from 'react-router';
import { Result } from 'ts-results-es';
import { zx } from 'zodix';

import { EmptySetMessage } from '~/components/content/empty-set-message';
import { useInfiniteScroll } from '~/components/infinite-scroll';
import { SearchInput, searchInputSchema } from '~/components/search-input';
import { type GymSearchResult } from '~/domain/gyms/models/gym';
import { searchGyms } from '~/domain/gyms/queries/search-gyms.server';
import { getCities } from '~/domain/shared/queries/get-cities.server';
import { getZipCodes } from '~/domain/shared/queries/get-zip-codes.server';
import { paginationSchema } from '~/domain/shared/schemas/filter-options';
import { type loader as favoritesLoader } from '~/routes/gym+/resources+/favorite';
import { handleDomainError } from '~/utils/errors.server';
import { parseCityStateZip } from '~/utils/parse-city-state-zip.server';

import { type Route } from './+types/_index';
import { GymCard } from './components/gym-card';
import { WelcomeSplash } from './components/welcome-splash';

export const handle = {
  title: 'Find Gyms & Classes',
};

const PAGE_SIZE = 50;
const CACHE_HEADERS = { headers: { 'Cache-Control': 'public, max-age=120' } };

export async function loader({ request }: Route.LoaderArgs) {
  const { searchParams } = new URL(request.url);
  const paginationOptionsResult = zx.parseQuerySafe(searchParams, paginationSchema);
  const { pageIndex, pageSize } = paginationOptionsResult.success
    ? paginationOptionsResult.data
    : { pageIndex: 0, pageSize: PAGE_SIZE };

  const { search, city: rawCity, lat, long, dist } = zx.parseQuery(request, searchInputSchema);

  const { city, state, zip } = parseCityStateZip(rawCity);

  const gymSearchParams = {
    search,
    city,
    stateAbbreviation: state,
    zip,
    pageIndex,
    pageSize,
    lat,
    long,
    dist,
  };

  // Optimization -- When infinite scrolling, only load gym data
  if (searchParams.has('scroll')) {
    const gymsResult = await searchGyms(gymSearchParams);
    const gyms = gymsResult.mapErr(handleDomainError).unwrap();
    return data({ gyms, cities: null, zipCodes: null }, CACHE_HEADERS);
  }

  // Otherwise get everything for the initial page load
  const [gymsResult, citiesResult, zipCodesResult] = await Promise.all([
    searchGyms(gymSearchParams),
    getCities(),
    getZipCodes(),
  ]);
  const result = Result.all(gymsResult, citiesResult, zipCodesResult);

  return result
    .map(([gyms, cities, zipCodes]) =>
      data(
        {
          gyms,
          cities,
          zipCodes,
        },
        CACHE_HEADERS,
      ),
    )
    .mapErr(handleDomainError)
    .unwrap();
}

const buildQuery = (searchParams: URLSearchParams, pageIndex: number) => {
  const query = new URLSearchParams(searchParams);
  query.set('pageIndex', String(pageIndex));
  query.set('pageSize', String(PAGE_SIZE));
  query.set('scroll', '1');
  return `?index&${query}`;
};

const buildReturnQuery = (searchParams: URLSearchParams) => {
  const searchParamsWithoutBlanks = new URLSearchParams();
  searchParams.forEach((value, key) => {
    if (value) {
      searchParamsWithoutBlanks.set(key, value);
    }
  });
  const gymLinkParams = new URLSearchParams();
  gymLinkParams.set('query', searchParamsWithoutBlanks.toString());
  return gymLinkParams.toString();
};

export const shouldRevalidate: ShouldRevalidateFunction = (data) => {
  // Gyms can be favorited from within the list. We don't want to reload the
  // entire list of gyms just because a gym was added/removed as a favorite
  const isFavoriteRequest = data.formAction === '/gym/resources/favorite';
  if (isFavoriteRequest) return false;

  return data.defaultShouldRevalidate; //Maintain default behavior for other requests
};

function SearchResults({
  title,
  results,
  returnQuery,
  handleFavoriteChange,
}: {
  title?: string;
  results: GymSearchResult[];
  returnQuery: string;
  handleFavoriteChange: () => void;
}) {
  return (
    <>
      {title ? <div className="px-8 text-subtitle2 tablet:px-12 desktop:px-0">{title}</div> : null}
      <div className="grid w-full gap-4 tablet:grid-cols-[repeat(auto-fill,minmax(350px,1fr))] tablet:px-12 desktop:px-0">
        {results.map((gym) => (
          <Link
            to={`/gym/${gym.urlSlug ?? gym.id}?${returnQuery}`}
            key={gym.id}
            className="flex flex-col"
          >
            <GymCard gym={gym} onFavoriteChange={handleFavoriteChange} className="flex-1" />
          </Link>
        ))}
      </div>
    </>
  );
}

export default function Search() {
  const [searchParams] = useSearchParams();
  const {
    gyms: { items: initialItems, totalCount: initialTotalCount },
    cities,
    zipCodes,
  } = useLoaderData<typeof loader>();
  const { data, state, load } = useFetcher<typeof loader>();
  const pageIndex = useRef(0);
  const [items, setItems] = useState(initialItems);
  const [totalCount, setTotalCount] = useState(initialTotalCount);
  const { load: loadGymFavorites } = useFetcher<typeof favoritesLoader>({
    key: 'favorite-gym-ids',
  });

  useEffect(() => {
    loadGymFavorites('/gym/resources/favorite');
  }, [loadGymFavorites]);

  const loadPage = useCallback(() => {
    pageIndex.current++;
    if (pageIndex.current * PAGE_SIZE < totalCount) {
      load(buildQuery(searchParams, pageIndex.current));
    }
  }, [totalCount, searchParams, load]);

  // User scrolled to a new page and new items were loaded, so append those to
  // the infinite-scroll state
  useEffect(() => {
    if (data && state === 'idle') {
      const { items, totalCount } = data.gyms;
      setItems((prev) => [...prev, ...items]);
      setTotalCount(totalCount);
    }
  }, [data, state]);

  // Search params were updated, so reinitialize the infinite-scroll state
  useEffect(() => {
    setItems(initialItems);
    setTotalCount(initialTotalCount);
    pageIndex.current = 0;
  }, [load, initialItems, initialTotalCount]);

  const comboboxItems = [
    ...cities!.map(({ city, state }) => `${city}, ${state}`),
    ...zipCodes!.map((z) => z?.toString() ?? ''),
  ];

  const loading = state === 'loading';
  const { loadingSentinel, spinner } = useInfiniteScroll({ loading, loadPage });

  const handleFavoriteChange = () => {
    loadGymFavorites('/gym/resources/favorite');
  };

  const returnQuery = buildReturnQuery(searchParams);

  const firstUnverifiedGym = items.findIndex((gym) => !gym.verified);
  const verifiedGyms = firstUnverifiedGym === -1 ? items : items.slice(0, firstUnverifiedGym);
  const unverifiedGyms = firstUnverifiedGym === -1 ? [] : items.slice(firstUnverifiedGym);

  const renderGyms = () => {
    if (items.length === 0) {
      return (
        <EmptySetMessage
          title="Sorry, no results match your search."
          message="Check your spelling or try other search terms."
        />
      );
    }

    const verifiedGymCards =
      verifiedGyms.length > 0 ? (
        <SearchResults
          title={unverifiedGyms.length > 0 ? 'Verified Gyms' : undefined}
          results={verifiedGyms}
          handleFavoriteChange={handleFavoriteChange}
          returnQuery={returnQuery}
        />
      ) : null;

    const unverifiedGymCards =
      unverifiedGyms.length > 0 ? (
        <SearchResults
          title={verifiedGyms.length > 0 ? 'Other Gyms' : undefined}
          results={unverifiedGyms}
          handleFavoriteChange={handleFavoriteChange}
          returnQuery={returnQuery}
        />
      ) : null;

    return (
      <>
        {verifiedGymCards}
        {verifiedGymCards && unverifiedGymCards ? <hr /> : null}
        {unverifiedGymCards}
        {loadingSentinel}
        {spinner}
      </>
    );
  };

  return (
    <div className="flex size-full flex-col">
      <div className="-mt-8 rounded-b-2xl bg-gray-200 px-2 pt-4 tablet:px-12">
        <div className="mx-auto mt-4 max-w-screen-xl">
          <Form>
            <div className="py-2">
              <SearchInput
                namePlaceholder="Gym Name"
                comboboxItems={comboboxItems}
                searchParams={searchParams}
                className="px-4 tablet:py-2"
              />
            </div>
            <button type="submit" className="hidden">
              {/* this is only here to make the form submit when the user presses enter */}
            </button>
          </Form>
        </div>
      </div>

      <div className="mx-auto flex size-full max-w-screen-xl flex-col gap-y-6 pt-6">
        {renderGyms()}
      </div>

      <WelcomeSplash />
    </div>
  );
}
